<script setup>
import { computed, ref } from 'vue';
import { pinTypeMeta } from '../utils/pinMeta';

const props = defineProps({
  map: { type: Object, required: true },
  pins: { type: Array, default: () => [] },
  activeTypes: {
    type: Array,
    default: () => ['cabo', 'grupo', 'evento', 'area'],
  },
});
const emit = defineEmits(['clickCoord', 'editPin']);

const canvasRef = ref(null);
const hoveredName = ref('');

const visiblePins = computed(() =>
  props.pins.filter(p => props.activeTypes.includes(p.pinType))
);

function pinXY(pin) {
  const bbox = props.map.bbox || {};
  if (!bbox.minLng) return { left: '50%', top: '50%' };
  const x = ((pin.lng - bbox.minLng) / (bbox.maxLng - bbox.minLng)) * 100;
  const y = ((bbox.maxLat - pin.lat) / (bbox.maxLat - bbox.minLat)) * 100;
  return { left: `${x}%`, top: `${y}%` };
}

function handleClick(e) {
  const rect = canvasRef.value.getBoundingClientRect();
  const xPct = (e.clientX - rect.left) / rect.width;
  const yPct = (e.clientY - rect.top) / rect.height;
  const bbox = props.map.bbox || {};
  if (!bbox.minLng) return;
  const lng = bbox.minLng + xPct * (bbox.maxLng - bbox.minLng);
  const lat = bbox.maxLat - yPct * (bbox.maxLat - bbox.minLat);
  emit('clickCoord', { lat, lng });
}

function onSvgMouseMove(e) {
  const target = e.target;
  if (target?.classList?.contains('eb-neigh')) {
    hoveredName.value = target.getAttribute('data-name') || '';
  } else {
    const placeGroup = target?.closest?.('.eb-place');
    if (placeGroup) {
      hoveredName.value = placeGroup.getAttribute('data-name') || '';
    } else {
      hoveredName.value = '';
    }
  }
}
</script>

<template>
  <div
    ref="canvasRef"
    class="relative aspect-[4/3] w-full overflow-hidden rounded-xl border border-n-strong/40 bg-gradient-to-br from-slate-50 to-blue-50 shadow-inner"
    @click="handleClick"
    @mousemove="onSvgMouseMove"
  >
    <!-- SVG layer -->
    <div class="pointer-events-none absolute inset-0" v-html="map.svg" />

    <!-- Pins layer -->
    <div class="pointer-events-none absolute inset-0">
      <button
        v-for="pin in visiblePins"
        :key="pin.id"
        type="button"
        :style="{ ...pinXY(pin), transform: 'translate(-50%, -100%)' }"
        class="pointer-events-auto absolute flex flex-col items-center transition hover:scale-110"
        :title="pin.title"
        @click.stop="$emit('editPin', pin)"
      >
        <span
          class="flex size-7 items-center justify-center rounded-full ring-2 ring-white shadow-lg"
          :class="[pinTypeMeta(pin.pinType).color]"
        >
          <span
            :class="pinTypeMeta(pin.pinType).icon"
            class="size-3.5 text-white"
          />
        </span>
        <span
          class="-mt-0.5 size-2 rotate-45 rounded-sm"
          :class="pinTypeMeta(pin.pinType).color"
        />
      </button>
    </div>

    <!-- Hovered neighborhood label -->
    <div
      v-if="hoveredName"
      class="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-md bg-black/70 px-2 py-1 text-[11px] font-semibold text-white shadow"
    >
      {{ hoveredName }}
    </div>

    <!-- Hint -->
    <div
      class="pointer-events-none absolute top-3 right-3 rounded-md bg-white/80 px-2 py-1 text-[10px] text-n-slate-11"
    >
      Clique no mapa para adicionar pin
    </div>
  </div>
</template>

<style scoped>
:deep(.eb-boundary) {
  fill: rgba(59, 130, 246, 0.08);
  stroke: rgb(30, 64, 175);
  stroke-width: 3;
  stroke-linejoin: round;
}
:deep(.eb-neigh) {
  fill: rgba(148, 163, 184, 0.08);
  stroke: rgb(71, 85, 105);
  stroke-width: 1;
  transition: fill 0.15s;
  cursor: pointer;
  pointer-events: all;
}
:deep(.eb-neigh:hover) {
  fill: rgba(59, 130, 246, 0.25);
}
:deep(.eb-place circle) {
  fill: rgb(244, 63, 94);
  stroke: white;
  stroke-width: 2;
  cursor: pointer;
  pointer-events: all;
  transition: r 0.15s;
}
:deep(.eb-place circle:hover) {
  r: 10;
}
:deep(.eb-place text) {
  font-size: 14px;
  font-weight: 600;
  fill: rgb(30, 41, 59);
  paint-order: stroke;
  stroke: white;
  stroke-width: 3;
  pointer-events: none;
}
</style>
